import type { CalendarApi, CalendarOptions, EventInput } from '@fullcalendar/core';
import esLocale from '@fullcalendar/core/locales/es';
import type { FullCalendarComponent } from '@fullcalendar/angular';

const MOBILE_BREAKPOINT = 576;
const TABLET_BREAKPOINT = 992;
const DESKTOP_DAY_MAX_EVENT_ROWS = 4;
const MOBILE_DAY_MAX_EVENT_ROWS = 3;

type ResponsiveFullCalendarView = 'dayGridDay' | 'dayGridWeek' | 'dayGridMonth';

const getViewportWidth = (): number => {
    if (typeof window === 'undefined') {
        return TABLET_BREAKPOINT;
    }

    return window.innerWidth;
};

const getResponsiveFullCalendarView = (): ResponsiveFullCalendarView => {
    const viewportWidth = getViewportWidth();

    if (viewportWidth < MOBILE_BREAKPOINT) {
        return 'dayGridDay';
    }

    if (viewportWidth < TABLET_BREAKPOINT) {
        return 'dayGridWeek';
    }

    return 'dayGridMonth';
};

const getResponsiveHeaderToolbar = (): NonNullable<CalendarOptions['headerToolbar']> => {
    if (getViewportWidth() < MOBILE_BREAKPOINT) {
        return {
            left: 'prev,next',
            center: 'title',
            right: 'today'
        };
    }

    return {
        left: 'prev,next today',
        center: 'title',
        right: 'dayGridMonth,dayGridWeek,dayGridDay'
    };
};

const normalizeEventAsAllDay = (eventInput: EventInput): EventInput => {
    const normalizedEvent: EventInput = {
        ...eventInput,
        allDay: true
    };

    if (normalizedEvent.start && normalizedEvent.end && normalizedEvent.start === normalizedEvent.end) {
        delete normalizedEvent.end;
    }

    return normalizedEvent;
};

export const getResponsiveFullCalendarOptions = (): Partial<CalendarOptions> => ({
    initialView: getResponsiveFullCalendarView(),
    locale: esLocale,
    headerToolbar: getResponsiveHeaderToolbar(),
    height: 'auto',
    contentHeight: 'auto',
    expandRows: true,
    dayMaxEventRows: DESKTOP_DAY_MAX_EVENT_ROWS,
    displayEventTime: false,
    eventDisplay: 'block',
    defaultAllDay: true,
    eventDataTransform: normalizeEventAsAllDay,
    views: {
        dayGridMonth: {
            dayMaxEventRows: DESKTOP_DAY_MAX_EVENT_ROWS
        },
        dayGridWeek: {
            dayMaxEventRows: DESKTOP_DAY_MAX_EVENT_ROWS
        },
        dayGridDay: {
            dayMaxEventRows: MOBILE_DAY_MAX_EVENT_ROWS
        }
    }
});

export const applyResponsiveFullCalendarView = (calendar?: FullCalendarComponent): void => {
    const calendarApi: CalendarApi | undefined = calendar?.getApi();

    if (!calendarApi) {
        return;
    }

    const responsiveView = getResponsiveFullCalendarView();

    if (calendarApi.view.type !== responsiveView) {
        calendarApi.changeView(responsiveView);
    }

    calendarApi.setOption('headerToolbar', getResponsiveHeaderToolbar());
    calendarApi.updateSize();
};
